import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import Logger from '@/common/utils/Logger';
import { htmlOnDemandTranslationAtom } from '@/state/atom/translationAtom';

import { RUNTIME_MARKER_KIND, RUNTIME_MESSAGE, RUNTIME_VIEW, getHtmlContentOrigin } from './htmlTranslationProtocol';
import { buildOnDemandCacheKey, translateOnDemandSources, translateTrSources } from './HtmlTranslationHandler';

const TAG = 'useHtmlRuntimeBridge';

const { ON_DEMAND_TRANSLATION_STATUS } = TranslationConstant;

// The field or the org setting changed since the page was opened: translating what is on screen
// makes no sense any more, the user is asked to reload (COMMSG030). Any other error is silent.
const RELOAD_ERROR_CODES = [
    Constant.TRANSLATION_API_ERROR_CODE.DATA_CHANGED,
    Constant.TRANSLATION_API_ERROR_CODE.NO_PERMISSION
];

/**
 * Parent side of the HTML field translation runtime (MLT-001-F3).
 *
 * Active only when the backend served the translation copy, i.e. it returned a sourceLanguage
 * and a targetLanguage for this viewer. Otherwise the iframe shows the original and this hook
 * does nothing.
 *
 *   READY  -> translate the data-tr texts and APPLY them (no toggle, BR-7)
 *   toggle -> translate the data-tr-ondmd texts once per session and APPLY / SET_VIEW (BR-10)
 *   toggle error -> toggle stays OFF on the original; stale data opens the reload modal
 *
 * @param {Object} params
 * @param {Object} params.iframeRef - ref of the iframe showing the HTML.
 * @param {string} params.ticketId
 * @param {string} params.fieldId
 * @param {string|null} params.sourceLanguage - detected language of the HTML.
 * @param {string|null} params.targetLanguage - the viewer's 主言語.
 * @param {string} params.iframeSrc - a new src resets the bridge (new document, new runtime).
 * @returns {{
 *   onIframeLoad: Function,
 *   onDemand: {
 *     isVisible: boolean, isOn: boolean, status: string, toggle: Function,
 *     isReloadModalOpen: boolean, setIsReloadModalOpen: Function
 *   }
 * }}
 */
export default function useHtmlRuntimeBridge({ iframeRef, ticketId, fieldId, sourceLanguage, targetLanguage, iframeSrc }) {
    const isEnabled = Boolean(sourceLanguage && targetLanguage && ticketId && fieldId);
    const contentOrigin = getHtmlContentOrigin();

    const [onDemandSources, setOnDemandSources] = useState([]);
    const [onDemandStatus, setOnDemandStatus] = useState(ON_DEMAND_TRANSLATION_STATUS.IDLE);
    const [isOnDemandOn, setIsOnDemandOn] = useState(false);
    const [isReloadModalOpen, setIsReloadModalOpen] = useState(false);
    const [onDemandCache, setOnDemandCache] = useRecoilState(htmlOnDemandTranslationAtom);

    // data-tr texts already translated for the current document (READY can arrive twice: on load
    // and in answer to PING).
    const appliedTrSignatureRef = useRef('');

    const postToRuntime = useCallback(
        message => {
            const contentWindow = iframeRef?.current?.contentWindow;
            if (!contentWindow || !contentOrigin) return;
            contentWindow.postMessage(message, contentOrigin);
        },
        [iframeRef, contentOrigin]
    );

    // New document -> the toggle starts OFF on the original (BR-16).
    useEffect(() => {
        appliedTrSignatureRef.current = '';
        setOnDemandSources([]);
        setOnDemandStatus(ON_DEMAND_TRANSLATION_STATUS.IDLE);
        setIsOnDemandOn(false);
    }, [iframeSrc]);

    const applyTrTranslations = useCallback(
        async trSources => {
            const signature = trSources.join('\u0000');
            if (!signature || appliedTrSignatureRef.current === signature) return;
            appliedTrSignatureRef.current = signature;

            try {
                const dict = await translateTrSources(trSources, sourceLanguage, targetLanguage);
                if (Object.keys(dict).length === 0) return;
                postToRuntime({ type: RUNTIME_MESSAGE.APPLY, kind: RUNTIME_MARKER_KIND.TR, dict });
            } catch (error) {
                // Fail-safe: the original stays; the next open tries again (BR-3b).
                appliedTrSignatureRef.current = '';
                Logger.error(`${TAG} applyTrTranslations`, error);
            }
        },
        [sourceLanguage, targetLanguage, postToRuntime]
    );

    useEffect(() => {
        if (!isEnabled) return;

        const onRuntimeMessage = event => {
            if (event.origin !== contentOrigin) return;
            if (!iframeRef?.current || event.source !== iframeRef.current.contentWindow) return;
            if (event.data?.type !== RUNTIME_MESSAGE.READY) return;

            const { trSources, ondmdSources } = event.data;
            setOnDemandSources(Array.isArray(ondmdSources) ? ondmdSources.filter(text => typeof text === 'string') : []);
            if (Array.isArray(trSources) && trSources.length > 0) {
                applyTrTranslations(trSources.filter(text => typeof text === 'string'));
            }
        };

        window.addEventListener('message', onRuntimeMessage);
        return () => window.removeEventListener('message', onRuntimeMessage);
    }, [isEnabled, contentOrigin, iframeRef, applyTrTranslations]);

    const onIframeLoad = useCallback(() => {
        if (!isEnabled) return;
        postToRuntime({ type: RUNTIME_MESSAGE.PING });
    }, [isEnabled, postToRuntime]);

    const toggleOnDemand = useCallback(async () => {
        if (onDemandStatus === ON_DEMAND_TRANSLATION_STATUS.LOADING) return;

        if (isOnDemandOn) {
            postToRuntime({ type: RUNTIME_MESSAGE.SET_VIEW, kind: RUNTIME_MARKER_KIND.ONDMD, view: RUNTIME_VIEW.ORIGINAL });
            setIsOnDemandOn(false);
            return;
        }

        const cacheKey = buildOnDemandCacheKey(ticketId, fieldId, targetLanguage);
        let dict = onDemandCache[cacheKey];
        if (!dict) {
            setOnDemandStatus(ON_DEMAND_TRANSLATION_STATUS.LOADING);
            const { translations, errorCode } = await translateOnDemandSources(ticketId, fieldId, onDemandSources);
            if (!translations) {
                // Fail-safe: stay on the original, the toggle stays OFF.
                setOnDemandStatus(ON_DEMAND_TRANSLATION_STATUS.ERROR);
                if (RELOAD_ERROR_CODES.includes(errorCode)) setIsReloadModalOpen(true);
                return;
            }
            dict = translations;
            setOnDemandCache(previous => ({ ...previous, [cacheKey]: translations }));
        }

        postToRuntime({
            type: RUNTIME_MESSAGE.APPLY,
            kind: RUNTIME_MARKER_KIND.ONDMD,
            dict,
            view: RUNTIME_VIEW.TRANSLATION
        });
        setOnDemandStatus(ON_DEMAND_TRANSLATION_STATUS.TRANSLATED);
        setIsOnDemandOn(true);
    }, [
        onDemandStatus,
        isOnDemandOn,
        ticketId,
        fieldId,
        targetLanguage,
        onDemandCache,
        onDemandSources,
        postToRuntime,
        setOnDemandCache
    ]);

    return {
        onIframeLoad,
        onDemand: {
            isVisible: isEnabled && onDemandSources.length > 0,
            isOn: isOnDemandOn,
            status: onDemandStatus,
            toggle: toggleOnDemand,
            isReloadModalOpen,
            setIsReloadModalOpen
        }
    };
}
